import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getWeeks, createWeek, closeWeek, openWeek, deleteWeek } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  Plus,
  Calendar as CalendarIcon,
  TrendingUp,
  TrendingDown,
  Wallet,
  Lock,
  LockOpen,
  Loader2,
  Trash2,
  Banknote,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export default function WeeksPage() {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const fetchWeeks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getWeeks();
      setWeeks(data);
    } catch (err) {
      console.error('Failed to fetch weeks:', err);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWeeks();
  }, [fetchWeeks]);

  const handleCreate = async () => {
    if (!startDate || !endDate) return;

    try {
      setCreating(true);
      await createWeek({
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
      });
      toast.success('Неделя создана');
      setDialogOpen(false);
      setStartDate(null);
      setEndDate(null);
      fetchWeeks();
    } catch (err) {
      console.error('Failed to create week:', err);
      toast.error(err.response?.data?.detail || 'Ошибка создания недели');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = async (e, weekId) => {
    e.stopPropagation();
    try {
      await closeWeek(weekId);
      toast.success('Неделя закрыта');
      fetchWeeks();
    } catch (err) {
      console.error('Failed to close week:', err);
      toast.error(err.response?.data?.detail || 'Ошибка закрытия недели');
    }
  };

  const handleOpen = async (e, weekId) => {
    e.stopPropagation();
    try {
      await openWeek(weekId);
      toast.success('Неделя открыта');
      fetchWeeks();
    } catch (err) {
      console.error('Failed to open week:', err);
      toast.error(err.response?.data?.detail || 'Ошибка открытия недели');
    }
  };

  const handleDelete = async (e, weekId) => {
    e.stopPropagation();
    if (!window.confirm('Удалить неделю и все связанные данные?')) return;

    try {
      await deleteWeek(weekId);
      toast.success('Неделя удалена');
      fetchWeeks();
    } catch (err) {
      console.error('Failed to delete week:', err);
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const formatDateRange = (start, end) => {
    const s = parseISO(start);
    const e = parseISO(end);
    return `${format(s, 'd MMM', { locale: ru })} - ${format(e, 'd MMM yyyy', { locale: ru })}`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value) + ' $';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Недели</h1>
          <p className="text-muted-foreground">Управление учётными периодами</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setDialogOpen(true)} data-testid="create-week-btn">
            <Plus className="h-4 w-4 mr-2" />
            Создать
          </Button>
        )}
      </div>

      {weeks.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CalendarIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Нет недель</h3>
            <p className="text-muted-foreground mb-4">Создайте первую неделю для начала учёта</p>
            {isAdmin && (
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Создать неделю
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {weeks.map((week) => (
            <Card
              key={week.id}
              className="card-hover"
              onClick={() => navigate(`/weeks/${week.id}`)}
              data-testid={`week-card-${week.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-medium">{formatDateRange(week.start_date, week.end_date)}</p>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mt-1',
                        week.status === 'open' 
                          ? 'bg-green-500/10 text-green-500' 
                          : 'bg-red-500/10 text-red-500'
                      )}
                    >
                      {week.status === 'open' ? (
                        <>
                          <LockOpen className="h-3 w-3" />
                          Открыта
                        </>
                      ) : (
                        <>
                          <Lock className="h-3 w-3" />
                          Закрыта
                        </>
                      )}
                    </span>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      {week.status === 'open' ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-green-500 hover:text-green-600 hover:bg-green-500/10"
                          onClick={(e) => handleClose(e, week.id)}
                          title="Закрыть неделю"
                          data-testid={`close-week-${week.id}`}
                        >
                          <LockOpen className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={(e) => handleOpen(e, week.id)}
                          title="Открыть неделю"
                          data-testid={`open-week-${week.id}`}
                        >
                          <Lock className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={(e) => handleDelete(e, week.id)}
                        data-testid={`delete-week-${week.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-emerald-500" />
                    <div>
                      <p className="text-muted-foreground text-xs">Доход</p>
                      <p className="font-medium income-text">{formatCurrency(week.total_income)}</p>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <TrendingDown className="h-4 w-4 text-red-500" />
                      <div>
                        <p className="text-muted-foreground text-xs">Расход</p>
                        <p className="font-medium expense-text">{formatCurrency(week.total_expense)}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Banknote className="h-4 w-4 text-amber-500" />
                    <div>
                      <p className="text-muted-foreground text-xs">Зарплаты</p>
                      <p className="font-medium">{formatCurrency(week.total_salaries)}</p>
                    </div>
                  </div>
                </div>

                {isAdmin && (
                  <div className="mt-4 pt-3 border-t">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Wallet className="h-4 w-4 text-primary" />
                        <span className="text-sm text-muted-foreground">Прибыль</span>
                      </div>
                      <span
                        className={cn(
                          'font-semibold',
                          week.net_profit >= 0 ? 'profit-positive' : 'profit-negative'
                        )}
                      >
                        {week.net_profit >= 0 ? '+' : ''}{formatCurrency(week.net_profit)}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Week Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Создать неделю</DialogTitle>
            <DialogDescription>
              Выберите период для новой учётной недели
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Дата начала</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !startDate && 'text-muted-foreground'
                    )}
                    data-testid="start-date-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP', { locale: ru }) : 'Выберите дату'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    locale={ru}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Дата окончания</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !endDate && 'text-muted-foreground'
                    )}
                    data-testid="end-date-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP', { locale: ru }) : 'Выберите дату'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    disabled={(date) => startDate && date < startDate}
                    locale={ru}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!startDate || !endDate || creating}
              data-testid="confirm-create-week"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Создание...
                </>
              ) : (
                'Создать'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
